import type { TelegramSettingsUpdate, TelegramSettingsView } from "@screener/protocol";

/**
 * Черновик блока Telegram §6.6 экрана «Алерты» (design.md решение 8). Чистые
 * функции без React: панель держит `TelegramDraft` в состоянии контроллера,
 * а `telegramPatch` переводит его в тело `PATCH /api/telegram` — только
 * отличающиеся от прочитанного поля, чтобы не стирать незаданный токен и не
 * трогать промежуток, который пользователь не менял.
 */

export interface TelegramDraft {
  /** Токен бота — только пока его вводят; после ответа демона очищается. */
  token: string;
  chatId: string;
  interval: string;
}

/** Целое число секунд, `0` или больше — `i32::MAX` на демоне (`checked_telegram_update`). */
const INTERVAL_PATTERN = /^\d+$/;
const MAX_INTERVAL_SEC = 2147483647;

const CHAT_ID_CLEARED_ERROR =
  "Chat id нельзя стереть сохранением — чтобы отключить Telegram, нажмите «Отключить Telegram».";
const INTERVAL_ERROR = "Промежуток — целое число секунд, 0 или больше.";

/** Черновик из прочитанного вида: токен пуст, chat id и промежуток — как на демоне. */
export function draftFromView(view: TelegramSettingsView): TelegramDraft {
  return {
    token: "",
    chatId: view.chat_id ?? "",
    interval: String(view.min_interval_sec),
  };
}

/**
 * Ошибка поля до отправки — заданный chat id нельзя стереть сохранением
 * (для этого есть «Отключить Telegram»), промежуток обязан быть целым
 * неотрицательным числом в границах `i32`, иначе демон отвергнет то же
 * значение той же причиной.
 */
export function validateTelegramDraft(
  view: TelegramSettingsView,
  draft: TelegramDraft,
): string | null {
  const chatId = draft.chatId.trim();
  if (chatId === "" && view.chat_id !== null) {
    return CHAT_ID_CLEARED_ERROR;
  }
  const interval = draft.interval.trim();
  if (!INTERVAL_PATTERN.test(interval) || Number(interval) > MAX_INTERVAL_SEC) {
    return INTERVAL_ERROR;
  }
  return null;
}

/**
 * Тело `PATCH /api/telegram` — только отличающиеся поля. Вызывается уже
 * после `validateTelegramDraft`: сама функция не проверяет годность полей,
 * только сравнивает черновик с прочитанным видом.
 */
export function telegramPatch(
  view: TelegramSettingsView,
  draft: TelegramDraft,
): Partial<TelegramSettingsUpdate> {
  const patch: Partial<TelegramSettingsUpdate> = {};

  const token = draft.token.trim();
  if (token !== "") {
    patch.bot_token = token;
  }

  const chatId = draft.chatId.trim();
  if (chatId !== (view.chat_id ?? "")) {
    patch.chat_id = chatId;
  }

  const interval = draft.interval.trim();
  if (INTERVAL_PATTERN.test(interval)) {
    const parsed = Number(interval);
    if (parsed !== view.min_interval_sec) {
      patch.min_interval_sec = parsed;
    }
  }

  return patch;
}
